// pages/FaqPage.jsx
import React, { useState, useEffect } from 'react';
import { faqService } from '../../services/faqService';
import { HelpCircle, Search, ArrowRight, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const styles = {
    primaryColor: '#F76707',
    secondaryColor: '#0A6EBD',
    lightOrange: '#FFF4E6',
};

const FaqPage = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedItems, setExpandedItems] = useState({});
    const [filteredCategories, setFilteredCategories] = useState([]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const result = await faqService.getAllCategories(true);
                if (result.success) {
                    // Filtrer pour n'inclure que les catégories et items actifs
                    const activeCategories = result.data
                        .filter(category => category.isActive)
                        .map(category => ({
                            ...category,
                            items: category.items
                                ? category.items.filter(item => item.isActive).sort((a, b) => a.displayOrder - b.displayOrder)
                                : []
                        }))
                        .filter(category => category.items.length > 0)
                        .sort((a, b) => a.displayOrder - b.displayOrder);

                    setCategories(activeCategories);
                    setFilteredCategories(activeCategories);
                }
            } catch (error) {
                console.error('Erreur lors du chargement des FAQ:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, []);

    // Filtrage des FAQ en fonction de la recherche
    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredCategories(categories);
            return;
        }

        const term = searchTerm.toLowerCase();
        const filtered = categories
            .map(category => {
                // Filtrer les items qui correspondent à la recherche
                const matchedItems = category.items.filter(
                    item =>
                        item.question.toLowerCase().includes(term) ||
                        item.answer.toLowerCase().includes(term)
                );

                // Ne retourner la catégorie que si elle a des items correspondants
                if (matchedItems.length > 0) {
                    return {
                        ...category,
                        items: matchedItems
                    };
                }
                return null;
            })
            .filter(Boolean); // Éliminer les catégories null (sans items correspondants)

        setFilteredCategories(filtered);
    }, [searchTerm, categories]);

    const toggleItem = (categoryId, itemId) => {
        setExpandedItems(prev => {
            const key = `${categoryId}-${itemId}`;
            return {
                ...prev,
                [key]: !prev[key]
            };
        });
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    return (
        <div className="min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
            <div
                className="py-4 text-white"
                style={{ background: styles.secondaryColor }}
            >
                <div className="container">
                    <div className="d-flex align-items-center mb-2">
                        <Link to="/" className="text-white text-decoration-none d-flex align-items-center">
                            <ArrowLeft size={20} className="me-2" />
                            Retour à l'accueil
                        </Link>
                    </div>
                    <h1 className="h3 mb-0">Foire Aux Questions</h1>
                </div>
            </div>

            <div className="container py-5">
                <div className="row mb-5">
                    <div className="col-lg-6 mx-auto">
                        <div className="input-group">
                            <span className="input-group-text bg-white border-end-0">
                                <Search size={20} />
                            </span>
                            <input
                                type="text"
                                className="form-control border-start-0"
                                placeholder="Rechercher dans les FAQ..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                            />
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center p-5">
                        <div className="spinner-border" role="status" style={{ color: styles.primaryColor }}>
                            <span className="visually-hidden">Chargement...</span>
                        </div>
                        <p className="mt-3">Chargement des questions fréquentes...</p>
                    </div>
                ) : filteredCategories.length === 0 ? (
                    <div className="text-center p-5">
                        <HelpCircle size={48} className="mb-3" style={{ color: styles.secondaryColor }} />
                        <h3 className="h5 mb-3">Aucun résultat trouvé</h3>
                        <p className="text-muted">
                            Aucune réponse ne correspond à votre recherche. Essayez avec d'autres termes ou
                            contactez notre service client pour obtenir de l'aide.
                        </p>
                    </div>
                ) : (
                    <div className="row">
                        {filteredCategories.map((category) => (
                            <div key={category.id} className="col-12 mb-4">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-header bg-white py-3">
                                        <h2 className="h5 mb-0" style={{ color: styles.secondaryColor }}>
                                            {category.name}
                                        </h2>
                                        {category.description && (
                                            <p className="text-muted mb-0 mt-1 small">{category.description}</p>
                                        )}
                                    </div>
                                    <div className="card-body p-0">
                                        <div className="list-group list-group-flush">
                                            {category.items.map((item) => {
                                                const itemKey = `${category.id}-${item.id}`;
                                                const isExpanded = !!expandedItems[itemKey];

                                                return (
                                                    <div key={item.id} className="list-group-item border-0 border-bottom">
                                                        <div
                                                            className="d-flex justify-content-between align-items-center py-2"
                                                            onClick={() => toggleItem(category.id, item.id)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <h3 className="h6 mb-0 flex-grow-1">{item.question}</h3>
                                                            <ArrowRight
                                                                size={20}
                                                                style={{
                                                                    transform: isExpanded ? 'rotate(90deg)' : 'none',
                                                                    transition: 'transform 0.3s ease'
                                                                }}
                                                            />
                                                        </div>
                                                        {isExpanded && (
                                                            <div className="py-3" style={{ whiteSpace: 'pre-line' }}>
                                                                {item.answer}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FaqPage;